import { FC, useContext, useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/system';

import { routes } from 'src/constants/routes';
import { UserDetailsPartial } from 'src/auth/types';
import { userDataKeys } from 'src/features/details-form/constants';
import { UserDataFormValues } from 'src/features/details-form/types';
import { createUserDetailsSchema } from 'src/features/details-form/user-details-schema';
import { useGetUserInfo } from 'src/lib/hooks/use-get-user-info';
import { validateEmail } from 'src/utils/validators/validate-email';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { Spinner } from 'src/components/spinner';
import { getAddressDefaults } from 'src/utils/user-address';
import { Building } from 'src/shared/types/building';
import { ApiBasket } from 'src/api/basketService';

import { AuthContext } from '../../../providers/auth-context';
import { useGetUserHasBookings } from '../../../lib/hooks/use-get-user-has-bookings';
import { parseThirdPartyPhone } from '../../../utils/parse-third-party-phone';
import { useUrlParams } from '../../../utils/use-url-params';
import { useUserFormSubmit } from '../hooks/use-user-form-submit';
import { useGetUserExists } from '../hooks/user/use-get-user-exists';
import { ErrorSnackbarContext } from '../../error-snackbar/error-snackbar-context';
import { ErrorSnackbarActionType } from '../../error-snackbar/types';

import { UserFormView } from './user-form.view';
import { styles } from './styles';
import { AgentUserDetails } from './types';

interface UserFormProps {
    building: Building;
    basket: ApiBasket;
}

export const UserFormContainer: FC<UserFormProps> = ({ building, basket }) => {
    const userState = useContext(AuthContext);
    const dispatchSnackbarError = useContext(ErrorSnackbarContext);
    const [showDetailsForm, setShowDetailsForm] = useState<boolean>(false);
    const urlParams = useUrlParams();
    const navigate = useNavigate();

    const userLoggedIn = userState.loggedIn;
    const userDetails = userState.session?.user.user_metadata as UserDetailsPartial;

    const [isLoginDialogOpen, setIsLoginDialogOpen] = useState<boolean>(false);

    const {
        data: userExists,
        mutateAsync: fetchUserExistsAsync,
        mutate: fetchUserExists,
        isPending: isUserExistsPending,
    } = useGetUserExists();
    const {
        data: userHasBookings,
        isLoading: isGettingUserBookings,
        isError: isErrorGettingUserBookings,
    } = useGetUserHasBookings(userState.loggedIn);
    const { data: userInfo, isPending: isGettingUserInfo } = useGetUserInfo(userState.loggedIn);

    const userInfoEnabled = userState.loggedIn;

    const toggleLoginDialog = () => {
        setIsLoginDialogOpen((prev) => (userLoggedIn ? false : !prev));
    };

    if (!building) throw new Error(ErrorMessage.NO_BOOKING_DETAILS_PROVIDED);

    const userDetailsSchema = createUserDetailsSchema(
        !userLoggedIn,
        building.spanish_tax_applicable
    );

    const agentUserDetails: AgentUserDetails = {
        firstName: urlParams.firstName,
        lastName: urlParams.lastName,
        preferredName: urlParams.preferredName,
        email: urlParams.email,
        phone: parseThirdPartyPhone(urlParams.callingCode, urlParams.phone),
        address1: urlParams.address1,
        address2: urlParams.address2,
        city: urlParams.city,
        postCode: urlParams.postCode,
        country: urlParams.country,
        dateOfBirth: urlParams.parsedDob,
        university: urlParams.parsedUniversity,
    };

    const defaultUserData = {
        ...agentUserDetails,
        ...userDetails,
        ...getAddressDefaults(userDetails, userInfo, agentUserDetails),
        nifNumber: userDetails?.nifNumber ?? userInfo?.nif_number ?? null,
        nieNumber: userDetails?.nieNumber ?? userInfo?.nie_number ?? null,
        passportNumber: userDetails?.passportNumber ?? userInfo?.passport_number ?? null,
        passportIssuingCountry:
            userDetails?.passportIssuingCountry ?? userInfo?.passport_issuing_country ?? null,
    };

    const methods = useForm<UserDataFormValues>({
        resolver: yupResolver(userDetailsSchema) as Resolver<UserDataFormValues>,
        defaultValues: defaultUserData,
        mode: 'all',
    });

    const { handleSubmit, watch, reset } = methods;

    const currentEmail = watch(userDataKeys.email);

    useEffect(() => {
        if (userLoggedIn) {
            setIsLoginDialogOpen(false);
            reset(defaultUserData);
        }
    }, [userLoggedIn, userInfo]);

    const hasRoommates = basket.products[0].selected_students > 1;

    const { submitHandler, loading } = useUserFormSubmit({
        userLoggedIn,
        userId: userState.session?.user.id ?? null,
        basket,
        building,
        hasRoommates,
    });

    const onSubmit = handleSubmit(async (values) => {
        if (!userLoggedIn) {
            try {
                const exists = await fetchUserExistsAsync(values.email);
                if (exists) {
                    setIsLoginDialogOpen(true);
                    return;
                }
            } catch (error) {
                dispatchSnackbarError({
                    type: ErrorSnackbarActionType.SOMETHING_WENT_WRONG,
                    payload: null,
                });
                return;
            }
        }

        window.dataLayer.push({
            event: 'booking_details',
            city: values.city,
            building: building.building_slug,
            room_code: basket.products[0].room_code,
            academic_year: basket.products[0].academic_year,
            price: basket.products[0].price,
        });

        submitHandler(values);
    });

    if (isErrorGettingUserBookings) {
        navigate(routes.failure.index);
        return null;
    }

    const handleContinue = () => {
        fetchUserExists(currentEmail, {
            onSuccess: (exists) => {
                if (exists) {
                    setIsLoginDialogOpen(true);
                    return;
                }
                setShowDetailsForm(true);
            },
            onError: () => {
                dispatchSnackbarError({
                    type: ErrorSnackbarActionType.SOMETHING_WENT_WRONG,
                    payload: null,
                });
            },
        });
    };

    const validateEmailField = () => {
        const fieldValue = watch(userDataKeys.email);
        return !!fieldValue && validateEmail(fieldValue);
    };

    return isGettingUserBookings || (userInfoEnabled && isGettingUserInfo) ? (
        <Box css={styles.spinner}>
            <Spinner />
        </Box>
    ) : (
        <UserFormView
            methods={methods}
            showDetailsForm={showDetailsForm}
            userExists={!!userExists}
            userLoggedIn={userState.loggedIn}
            userHasBookings={userHasBookings ?? false}
            onContinue={handleContinue}
            isEmailValid={validateEmailField()}
            onSubmit={onSubmit}
            hasRoommates={hasRoommates}
            loading={loading}
            isLoginDialogOpen={isLoginDialogOpen}
            isUserExistsPending={isUserExistsPending}
            toggleLoginDialog={toggleLoginDialog}
            currentEmail={currentEmail}
            building={building}
        />
    );
};
